import { isCustomerRole } from "@/constants/customerNav";
/** ONE Conversations — right-side collaboration drawer.
 *
 *  Header icon opens this drawer.  Drawer contains conversation list + selected
 *  thread + composer.  Visibility indicator (Internal / Customer / Partner) is
 *  displayed in the composer at all times to prevent accidental leaks.
 *
 *  The drawer is designed to be embedded ANYWHERE — record 360 pages, the full
 *  workspace, and inside modals — via the `contextType` + `contextId` props.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, X, Send, Lock, Users, Truck, ChevronLeft, MoreHorizontal, CheckCheck, Loader2, Paperclip, File as FileIcon, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const VISIBILITY = {
  internal:        { label: "Internal",        icon: Lock,  tone: "bg-slate-800 text-white"                      },
  customer_shared: { label: "Customer shared", icon: Users, tone: "bg-[color:var(--st-accent)] text-white"       },
  partner_shared:  { label: "Partner shared",  icon: Truck, tone: "bg-emerald-700 text-white"                     },
};

export function ChatDrawerButton() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { user } = useAuth();

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/conversations/unread-count");
      setUnread(data?.unread || 0);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 60_000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="chat-drawer-open"
        aria-label={`Conversations${unread ? ` (${unread} unread)` : ""}`}
        className="relative h-9 w-9 grid place-items-center rounded-md text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]"
      >
        <MessageSquare className="h-4 w-4" strokeWidth={1.6} />
        {unread > 0 && (
          <span
            data-testid="chat-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[color:var(--st-accent)] text-white text-[10px] font-semibold grid place-items-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && <ChatDrawer onClose={() => { setOpen(false); refreshUnread(); }} />}
    </>
  );
}

export function ChatDrawer({ onClose, contextType, contextId, contextLabel }) {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(contextId ? "context" : "mine");

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (contextId) { params.context_type = contextType; params.context_id = contextId; }
      else if (filter !== "all") params.filter = filter;
      const { data } = await api.get("/conversations", { params });
      setConversations(data?.items || []);
      if (data?.items?.[0] && !selectedId) setSelectedId(data.items[0].id);
    } catch (e) {
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  }, [filter, contextId, contextType, selectedId]);

  useEffect(() => { loadList(); }, [loadList]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="chat-drawer">
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close chat"
        onClick={onClose}
        className="flex-1 bg-slate-900/30 backdrop-blur-[1px]"
      />
      {/* Panel */}
      <aside className="w-full max-w-[560px] bg-[color:var(--st-surface)] shadow-2xl border-l border-[color:var(--st-border)] flex flex-col h-full">
        <header className="h-12 border-b border-[color:var(--st-border)] px-4 flex items-center gap-3 shrink-0">
          {selectedId ? (
            <button onClick={() => setSelectedId(null)} className="text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]" data-testid="chat-back">
              <ChevronLeft className="h-4 w-4" />
            </button>
          ) : (
            <MessageSquare className="h-4 w-4 text-[color:var(--st-text-muted)]" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[color:var(--st-text)] truncate">
              {selectedId ? "Conversation" : (contextLabel || "Conversations")}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">
              ONE Conversations
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="chat-close"
            className="text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]">
            <X className="h-4 w-4" />
          </button>
        </header>

        {!selectedId ? (
          <ConversationList
            filter={filter}
            setFilter={setFilter}
            items={conversations}
            loading={loading}
            onSelect={setSelectedId}
            onCreated={loadList}
            contextType={contextType}
            contextId={contextId}
            contextLabel={contextLabel}
          />
        ) : (
          <ConversationThread
            conversationId={selectedId}
            onChange={loadList}
          />
        )}
      </aside>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function ConversationList({ filter, setFilter, items, loading, onSelect, onCreated, contextType, contextId, contextLabel }) {
  const [showNew, setShowNew] = useState(false);
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {!contextId && (
        <div className="flex items-center gap-1 px-3 py-2 border-b border-[color:var(--st-border)] overflow-x-auto shrink-0" data-testid="chat-filters">
          {["mine", "unread", "mentions", "customer", "internal", "closed"].map((f) => (
            <button key={f}
              onClick={() => setFilter(f)}
              data-testid={`chat-filter-${f}`}
              className={
                "h-6 px-2 rounded text-[11px] uppercase tracking-[0.1em] shrink-0 " +
                (filter === f
                  ? "bg-[color:var(--st-text)] text-white"
                  : "text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)]")
              }>
              {f}
            </button>
          ))}
          <div className="ml-auto shrink-0">
            <button onClick={() => setShowNew(true)} data-testid="chat-new-btn"
              className="h-6 px-2 rounded text-[11px] font-medium bg-[color:var(--st-text)] text-white hover:bg-[#17263C]">
              + New
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading && <div className="p-6 text-center text-[12px] text-[color:var(--st-text-muted)]"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-8 text-center">
            <MessageSquare className="h-6 w-6 mx-auto text-[color:var(--st-text-hint)]" />
            <div className="mt-2 text-[13px] text-[color:var(--st-text-muted)]">No conversations yet</div>
            <button
              onClick={() => setShowNew(true)} data-testid="chat-empty-new"
              className="mt-4 h-8 px-3 rounded-md bg-[color:var(--st-text)] text-white text-[12px] font-medium hover:bg-[#17263C]">
              Start a conversation
            </button>
          </div>
        )}
        {items.map((c) => (
          <button key={c.id}
            onClick={() => onSelect(c.id)}
            data-testid={`chat-conv-${c.id}`}
            className="w-full text-left px-4 py-3 border-b border-[color:var(--st-border)] hover:bg-[color:var(--st-surface-alt)]">
            <div className="flex items-center gap-2">
              <VisibilityBadge visibility={c.visibility} compact />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <div className={"text-[13px] truncate " + (c.unread ? "font-semibold text-[color:var(--st-text)]" : "text-[color:var(--st-text)]")}>{c.subject}</div>
                  {c.unread && <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--st-accent)] shrink-0" aria-label="unread" />}
                </div>
                <div className="text-[11px] text-[color:var(--st-text-muted)] truncate">
                  {c.context_label || `${c.context_type} · ${c.context_id || "general"}`}
                </div>
                {c.last_message_preview && (
                  <div className="text-[11px] text-[color:var(--st-text-subtle)] mt-1 truncate">{c.last_message_preview}</div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {showNew && (
        <NewConversationForm
          onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); onCreated(); onSelect(id); }}
          contextType={contextType}
          contextId={contextId}
          contextLabel={contextLabel}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function ConversationThread({ conversationId, onChange }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/conversations/${conversationId}`);
      setData(res);
      setVisibility(res?.conversation?.visibility || "internal");
      await api.post(`/conversations/${conversationId}/read`);
    } catch { toast.error("Failed to open conversation"); }
  }, [conversationId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const handleFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data: att } = await api.post(`/conversations/${conversationId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(att);
      }
      setAttachments((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) attached`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if ((!text.trim() && attachments.length === 0) || sending) return;
    setSending(true);
    try {
      await api.post(`/conversations/${conversationId}/messages`, {
        text: text.trim() || `Sent ${attachments.length} attachment(s).`,
        visibility,
        attachments,
      });
      setText("");
      setAttachments([]);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  if (!data) return <div className="flex-1 grid place-items-center text-[12px] text-[color:var(--st-text-muted)]"><Loader2 className="h-4 w-4 animate-spin mr-1" /> Loading…</div>;

  const isCustomer = isCustomerRole(user?.role);
  const isPartner  = user?.role?.startsWith("partner_");
  const allowedVis = isCustomer ? ["customer_shared"] : isPartner ? ["partner_shared"] : ["internal", "customer_shared", "partner_shared"];

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-4 py-3 border-b border-[color:var(--st-border)] bg-[color:var(--st-surface-alt)] shrink-0">
        <div className="flex items-center gap-2">
          <VisibilityBadge visibility={data.conversation.visibility} />
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-[color:var(--st-text)] truncate">{data.conversation.subject}</div>
            <div className="text-[11px] text-[color:var(--st-text-muted)]">
              {data.conversation.context_label || `${data.conversation.context_type}${data.conversation.context_id ? " · " + data.conversation.context_id : ""}`}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {!isCustomer && !isPartner && data.conversation.visibility === "internal" && (
              <button
                type="button"
                data-testid="chat-promote-visibility"
                onClick={async () => {
                  try {
                    await api.post(`/conversations/${conversationId}/visibility`, { visibility: "customer_shared" });
                    toast.success("Conversation is now visible to the customer");
                    await load();
                    onChange?.();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Could not promote visibility");
                  }
                }}
                title="Make this conversation visible to the customer participants"
                className="h-6 px-2 rounded text-[10px] uppercase tracking-[0.14em] font-medium border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100">
                Share with customer
              </button>
            )}
            <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-muted)]">
              {data.conversation.status}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
        {data.messages.length === 0 && (
          <div className="text-center text-[12px] text-[color:var(--st-text-muted)] pt-8">No messages yet — say hello.</div>
        )}
        {data.messages.map((m) => (
          <MessageBubble key={m.id} msg={m} isMine={m.author_user_id === user?.id} conversationId={conversationId} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={send} className="border-t border-[color:var(--st-border)] p-3 shrink-0">
        {allowedVis.length > 1 && (
          <div className="flex items-center gap-1.5 mb-2" data-testid="chat-visibility-selector">
            {allowedVis.map((v) => (
              <button key={v} type="button" onClick={() => setVisibility(v)}
                data-testid={`chat-vis-${v}`}
                className={
                  "h-6 px-2 rounded text-[10px] uppercase tracking-[0.14em] font-medium " +
                  (visibility === v ? VISIBILITY[v].tone : "border border-[color:var(--st-border)] text-[color:var(--st-text-muted)]")
                }>
                {VISIBILITY[v].label}
              </button>
            ))}
          </div>
        )}
        {visibility !== data.conversation.visibility && (
          <div data-testid="chat-visibility-warning"
            className="text-[11px] text-[color:var(--st-warn)] bg-[color:var(--st-warn-soft)] border border-[color:var(--st-warn-soft)] rounded-md px-2 py-1.5 mb-2">
            {visibility === "customer_shared"
              ? "This message will be visible to authorised customer users."
              : visibility === "internal"
              ? "This message is SUNTRONIX INTERNAL ONLY. Customers cannot see it."
              : "This message will be visible to the assigned partner."}
          </div>
        )}
        {attachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid="chat-attachments-preview">
            {attachments.map((a) => (
              <div key={a.id} className="inline-flex items-center gap-1.5 h-6 pl-2 pr-1 rounded bg-[color:var(--st-surface-alt)] border border-[color:var(--st-border)] text-[11px]">
                {a.mime?.startsWith("image/") ? <ImageIcon className="h-3 w-3" /> : <FileIcon className="h-3 w-3" />}
                <span className="max-w-[140px] truncate">{a.filename}</span>
                <button type="button" onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                  className="text-[color:var(--st-text-muted)] hover:text-[color:var(--st-critical)]" aria-label="Remove">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileInputRef} type="file" multiple hidden onChange={handleFilePick}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
            data-testid="chat-file-input" />
          <button type="button" onClick={() => fileInputRef.current?.click()}
            disabled={uploading} data-testid="chat-attach"
            className="h-9 w-9 rounded-md border border-[color:var(--st-border)] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)] grid place-items-center disabled:opacity-50"
            aria-label="Attach files">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }}
            placeholder="Write a message… ⌘/Ctrl+Enter to send"
            rows={2}
            data-testid="chat-composer"
            className="flex-1 min-w-0 rounded-md border border-[color:var(--st-border)] bg-white px-3 py-2 text-[13px] resize-none focus:outline-none focus:border-[color:var(--st-text)] focus:ring-2 focus:ring-[color:var(--st-text)]/10"
          />
          <button type="submit" disabled={(!text.trim() && attachments.length === 0) || sending}
            data-testid="chat-send"
            className="h-9 w-9 rounded-md bg-[color:var(--st-text)] text-white grid place-items-center disabled:opacity-50 hover:bg-[#17263C]">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}

function useAuthedBlobUrl(url, enabled = true) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  useEffect(() => {
    if (!enabled || !url) return;
    let cancelled = false;
    let objectUrl = null;
    setState("loading");
    (async () => {
      try {
        const res = await api.get(url, { responseType: "blob" });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
        setState("ready");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, enabled]);
  return { blobUrl, state };
}

function AttachmentPreview({ att, conversationId, isMine }) {
  const url = `/conversations/${conversationId}/attachments/${att.id}`;
  const isImage = att.mime?.startsWith("image/");
  const isPdf   = att.mime === "application/pdf";
  const { blobUrl, state } = useAuthedBlobUrl(url, isImage || isPdf);

  const openFull = () => {
    if (blobUrl) {
      window.open(blobUrl, "_blank", "noopener");
    } else {
      // fallback: trigger download-via-fetch
      (async () => {
        try {
          const res = await api.get(url, { responseType: "blob" });
          const u = URL.createObjectURL(res.data);
          window.open(u, "_blank", "noopener");
          setTimeout(() => URL.revokeObjectURL(u), 30_000);
        } catch { toast.error("Failed to download"); }
      })();
    }
  };

  if (isImage) {
    return (
      <button
        type="button"
        onClick={openFull}
        data-testid={`chat-attachment-image-${att.id}`}
        className="block relative rounded-md overflow-hidden border border-black/10 hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-white/50"
        style={{ maxWidth: 280 }}
        title={`${att.filename} · ${Math.round((att.size || 0) / 1024)} KB`}
      >
        {state === "ready" && blobUrl ? (
          <img src={blobUrl} alt={att.filename}
               className="block max-h-52 w-auto object-cover" />
        ) : (
          <div className={"w-52 h-32 grid place-items-center " + (isMine ? "bg-white/10" : "bg-white")}>
            {state === "error"
              ? <ImageIcon className="h-6 w-6 opacity-50" />
              : <Loader2 className="h-4 w-4 animate-spin opacity-70" />}
          </div>
        )}
        <div className={"absolute bottom-0 left-0 right-0 px-2 py-1 text-[10px] truncate " + (isMine ? "bg-black/40 text-white" : "bg-slate-900/70 text-white")}>
          {att.filename}
        </div>
      </button>
    );
  }

  if (isPdf) {
    return (
      <button
        type="button"
        onClick={openFull}
        data-testid={`chat-attachment-pdf-${att.id}`}
        className={"flex items-center gap-2 px-3 py-2 rounded-md border w-full max-w-[280px] " + (isMine ? "border-white/20 bg-white/10 hover:bg-white/15" : "border-[color:var(--st-border)] bg-white hover:bg-slate-50")}
      >
        <div className={"h-9 w-9 shrink-0 rounded grid place-items-center text-[10px] font-bold " + (isMine ? "bg-red-500/20 text-red-100" : "bg-red-50 text-red-700 border border-red-100")}>
          PDF
        </div>
        <div className="min-w-0 flex-1 text-left">
          <div className="text-[12px] font-medium truncate">{att.filename}</div>
          <div className="text-[10px] opacity-60">{Math.round((att.size || 0) / 1024)} KB · Click to open</div>
        </div>
      </button>
    );
  }

  // Generic file chip (existing behaviour)
  return (
    <button
      type="button"
      onClick={openFull}
      data-testid={`chat-attachment-file-${att.id}`}
      className={"flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border " + (isMine ? "border-white/20 hover:bg-white/10" : "border-[color:var(--st-border)] hover:bg-white")}
    >
      <FileIcon className="h-3 w-3" />
      <span className="truncate max-w-[220px]">{att.filename}</span>
      <span className="opacity-60">· {Math.round((att.size || 0) / 1024)} KB</span>
    </button>
  );
}

function MessageBubble({ msg, isMine, conversationId }) {
  const V = VISIBILITY[msg.visibility] || VISIBILITY.internal;
  const attachments = msg.attachments || [];
  const hasVisualAttachment = attachments.some((a) => a.mime?.startsWith("image/") || a.mime === "application/pdf");
  const isTextEmpty = !msg.text || /^Sent \d+ attachment/i.test(msg.text);
  return (
    <div className={"flex " + (isMine ? "justify-end" : "justify-start")}>
      <div className={"max-w-[85%] rounded-lg " + (hasVisualAttachment ? "px-2 py-2 " : "px-3 py-2 ") + (isMine ? "bg-[color:var(--st-text)] text-white" : "bg-[color:var(--st-surface-alt)] text-[color:var(--st-text)]")}>
        {!isMine && (
          <div className="text-[10px] uppercase tracking-[0.14em] opacity-70 mb-0.5 px-1">{msg.author_name}</div>
        )}
        {!isTextEmpty && (
          <div className="text-[13px] whitespace-pre-wrap px-1">{msg.text}</div>
        )}
        {attachments.length > 0 && (
          <div className={"space-y-1.5 " + (isTextEmpty ? "" : "mt-1.5")}>
            {attachments.map((a) => (
              <AttachmentPreview key={a.id} att={a} conversationId={conversationId} isMine={isMine} />
            ))}
          </div>
        )}
        <div className="mt-1 px-1 flex items-center gap-2 text-[10px] opacity-70">
          <V.icon className="h-3 w-3" />
          <span>{V.label}</span>
          <span>·</span>
          <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          {msg.edited_at && <span>· edited</span>}
        </div>
      </div>
    </div>
  );
}

function VisibilityBadge({ visibility, compact }) {
  const V = VISIBILITY[visibility] || VISIBILITY.internal;
  return (
    <span className={"inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] uppercase tracking-[0.14em] font-medium " + V.tone}
      data-testid={`vis-${visibility}`}>
      <V.icon className="h-3 w-3" />
      {!compact && V.label}
    </span>
  );
}

function NewConversationForm({ onClose, onCreated, contextType, contextId, contextLabel }) {
  const [subject, setSubject] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const isCustomer = isCustomerRole(user?.role);

  const create = async (e) => {
    e.preventDefault();
    if (!subject.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/conversations", {
        subject: subject.trim(),
        visibility: isCustomer ? "customer_shared" : visibility,
        context_type: contextType || "general",
        context_id: contextId,
        context_label: contextLabel,
      });
      onCreated(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t border-[color:var(--st-border)] p-4 bg-[color:var(--st-surface-alt)]">
      <form onSubmit={create} className="space-y-3">
        <input
          type="text" required autoFocus
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="What is this about?"
          data-testid="chat-new-subject"
          className="w-full h-9 px-3 rounded-md border border-[color:var(--st-border)] bg-white text-[13px] focus:outline-none focus:border-[color:var(--st-text)]"
        />
        {!isCustomer && (
          <div className="flex items-center gap-1.5">
            {["internal", "customer_shared", "partner_shared"].map((v) => (
              <button key={v} type="button" onClick={() => setVisibility(v)}
                data-testid={`chat-new-vis-${v}`}
                className={
                  "h-6 px-2 rounded text-[10px] uppercase tracking-[0.14em] font-medium " +
                  (visibility === v ? VISIBILITY[v].tone : "border border-[color:var(--st-border)] text-[color:var(--st-text-muted)]")
                }>
                {VISIBILITY[v].label}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="h-8 px-3 rounded-md border border-[color:var(--st-border)] text-[12px]">Cancel</button>
          <button type="submit" disabled={busy || !subject.trim()} data-testid="chat-new-create"
            className="h-8 px-3 rounded-md bg-[color:var(--st-text)] text-white text-[12px] disabled:opacity-50">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ChatDrawer;
